import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import {
  PhoneCall,
  PackagePlus,
  History,
  Clock,
  ChefHat,
  MonitorPlay,
  ShieldCheck,
  UserCircle,
  LogOut,
  RefreshCw,
  BookOpen,
  UtensilsCrossed,
} from "lucide-react";
import gif from "../assets/images/webp/giphy.webp";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "./authentication/AuthContext";
import api from "../utils/api";
import { globalAppCache } from "../utils/cache";

// ── New Orders Badge ────────────────────────────────────────────────────────
const NewOrdersBadge = ({ count }) => {
  if (!count || count === 0) return null;
  return (
    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center px-1 shadow-md animate-bounce ring-2 ring-white">
      {count > 99 ? "99+" : count}
    </span>
  );
};

// ── Tooltip Nav Item ─────────────────────────────────────────────────────────
// Wraps any nav icon; shows a smooth label tooltip on hover.
const NavItem = ({ to, label, badge, onClick, children }) => {
  const inner = (
    <div className="group relative flex flex-col items-center justify-center cursor-pointer p-2 rounded-xl hover:bg-slate-100/80 text-slate-600 hover:text-teal-600 transition-all duration-200">
      {/* Icon + badge overlay */}
      <div className="relative flex items-center justify-center">
        {children}
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center px-1 shadow-md animate-bounce ring-2 ring-white z-10">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>

      {/* Tooltip label — appears below on hover */}
      <span className="absolute top-full mt-2.5 whitespace-nowrap text-xs font-semibold bg-slate-800 text-white px-3 py-1.5 rounded-md shadow-xl opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 ease-out pointer-events-none z-50 border border-slate-700">
        {label}
        {badge > 0 && (
          <span className="ml-1.5 bg-rose-500 text-white rounded-full px-1.5 text-[10px] font-black">
            {badge}
          </span>
        )}
        {/* Arrow */}
        <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-800 border-t border-l border-slate-700 rotate-45 rounded-sm" />
      </span>
    </div>
  );

  if (onClick) {
    return (
      <button onClick={onClick} className="flex items-center">
        {inner}
      </button>
    );
  }

  return (
    <Link to={to} className="flex items-center">
      {inner}
    </Link>
  );
};

const Navbar = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [registrationType, setRegistrationType] = useState(null);
  const [storeName, setStoreName] = useState("");
  const [storeLogo, setStoreLogo] = useState(null);
  const [userId, setUserId] = useState("");
  const [newOrderCount, setNewOrderCount] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isStaff, staffRole, staffName, isImpersonating } = useAuth();

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleLogout = async () => {
    try {
      await api.post("/user/sessions/logout-current", {}, { timeout: 3000 });
    } catch {
      // offline or token already invalid — still log out locally
    }
    localStorage.removeItem("token");
    localStorage.removeItem("registrationType");
    localStorage.removeItem("user");
    window.location.href = "/";
  };

  const handleForceUpdate = async () => {
    try {
      // 1. Unregister all service workers
      if (navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      // 2. Clear all cache API data
      if (window.caches) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          await caches.delete(cacheName);
        }
      }
      // 3. Trigger hard reload
      window.location.reload();
    } catch (err) {
      console.error("Force update failed:", err);
      window.location.reload();
    }
  };

  const handleLogoClick = () => {
    !registrationType &&
      setRegistrationType(localStorage.getItem("registrationType"));

    if (isAuthenticated) {
      if (isStaff && staffRole === "chef") {
        navigate("/kds");
      } else if (registrationType === "restaurant") {
        navigate("/table");
      } else {
        navigate("/select-place");
      }
    } else {
      navigate("/");
    }
  };

  // Removed isLoggedOut useEffect as we now use window.location.href directly

  useEffect(() => {
    const type = localStorage.getItem("registrationType");
    setRegistrationType(type);

    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.user && decodedToken.user.id) {
          const id = decodedToken.user.id;
          setUserId(id);
          fetchStoreDetails(id);
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, [isAuthenticated]);

  const fetchStoreDetails = async (userId) => {
    // Optimistic cache load
    if (globalAppCache.userCache[userId]) {
      const adminDetails = globalAppCache.userCache[userId];
      setStoreName(adminDetails.shop_type || "Cloud Billing");
      setStoreLogo(adminDetails.restaurantLogo || gif);
    }

    try {
      const response = await api.get(`user/${userId}`);
      const adminDetails = response.data;
      globalAppCache.userCache[userId] = adminDetails; // Update cache
      setStoreName(adminDetails.shop_type || "Cloud Billing");
      setStoreLogo(adminDetails.restaurantLogo || gif);
    } catch (error) {
      console.error("Error fetching store name:", error);
    }
  };

  // ── New Orders Polling ──────────────────────────────────────────────────
  const fetchNewOrders = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get("/orders/active");
      // /orders/active returns { tables: { key: order }, parcels: [order] }
      const orders = [
        ...Object.values(res.data?.tables || {}),
        ...(res.data?.parcels || []),
      ];
      // Count orders with status ORDER_PLACED (newly placed, not yet being prepared)
      const newCount = orders.filter(
        (o) => o.status === "ORDER_PLACED"
      ).length;
      setNewOrderCount(newCount);
    } catch {
      // silently fail — don't break the navbar
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchNewOrders();
    const interval = setInterval(fetchNewOrders, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchNewOrders]);

  // Reset badge when user navigates to /process or /kds
  useEffect(() => {
    if (
      location.pathname === "/process" ||
      location.pathname === "/kds"
    ) {
      setNewOrderCount(0);
    }
  }, [location.pathname]);

  const isLoginPage =
    location.pathname === "/" || location.pathname === "/register";

  return (
    <div>
      <nav className={`bg-white w-full shadow-md py-1 px-4 flex items-center justify-between fixed ${isImpersonating ? 'top-8' : 'top-0'} z-[60]`}>
        {/* Left Section */}
        <div className="flex items-center">
          <button
            className="mr-4 text-xl block lg:hidden"
            onClick={toggleSidebar}
          >
            <i className="fas fa-bars"></i>
          </button>

          {!isAuthenticated && (
            <button
              onClick={handleLogoClick}
              className="focus:outline-none flex items-center gap-2"
            >
              <img width={50} height={50} src={gif} alt="Logo" className="max-h-12 max-w-[150px] w-auto object-contain" />
              <p className="text-xl font-bold font-serif truncate max-w-[150px]">
                Cloud Billing
              </p>
            </button>
          )}

          {isAuthenticated && (
            <button
              onClick={handleLogoClick}
              className="focus:outline-none flex items-center gap-2"
            >
              <img width={50} height={50} src={storeLogo} alt="Logo" className="max-h-12 max-w-[150px] w-auto object-contain" />
              <div className={`marquee ${storeName.length > 15 ? "scroll-enabled" : ""}`}>
                <div className="track">
                  <span>{storeName}&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  {storeName.length > 15 && (
                    <span>{storeName}&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  )}
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Right Section — Desktop */}
        <div className="hidden lg:flex items-center space-x-4">
          {isLoginPage && (
            <div className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition duration-300">
              Help Line:- 1234567890
            </div>
          )}

          {!isLoginPage && (
            <div className="flex items-center space-x-2">
              {/* Support */}
              <div className="flex items-center space-x-2 mr-2 text-slate-700">
                <PhoneCall size={20} className="text-teal-600" />
                <span className="font-bold">8570996916</span>
              </div>

              {/* Products */}
              {(!isStaff || staffRole === "manager") && (
                <NavItem to="/add-product" label="Products">
                  <PackagePlus size={24} />
                </NavItem>
              )}

              {/* Order History */}
              {(!isStaff || ["manager", "cashier"].includes(staffRole)) && (
                <NavItem to="/history" label="Order History">
                  <History size={24} />
                </NavItem>
              )}

              {/* Ledger */}
              {(!isStaff || ["manager", "cashier"].includes(staffRole)) && (
                <NavItem to="/ledger" label="Ledger">
                  <BookOpen size={24} />
                </NavItem>
              )}

              {/* Ongoing Orders */}
              {(!isStaff || ["manager", "cashier", "waiter"].includes(staffRole)) && (
                <NavItem to="/ongoing-orders" label="Ongoing Orders">
                  <Clock size={24} />
                </NavItem>
              )}

              {/* Captain View */}
              {(!isStaff || ["manager", "cashier", "waiter"].includes(staffRole)) && (
                <NavItem to="/captain" label="Captain">
                  <UtensilsCrossed size={24} />
                </NavItem>
              )}

              {/* Process — with NEW ORDERS badge */}
              {registrationType === "restaurant" &&
                (!isStaff ||
                  ["manager", "waiter", "chef", "cashier"].includes(staffRole)) && (
                  <NavItem to="/process" label="Process" badge={newOrderCount}>
                    <ChefHat size={24} />
                  </NavItem>
                )}

              {/* KDS — with NEW ORDERS badge */}
              {(!isStaff || ["manager", "chef"].includes(staffRole)) && (
                <NavItem to="/kds" label="KDS" badge={newOrderCount}>
                  <MonitorPlay size={24} />
                </NavItem>
              )}

              {/* Admin Panel */}
              {(!isStaff || staffRole === "manager") && (
                <NavItem to="/admin" label="Admin Panel">
                  <ShieldCheck size={24} />
                </NavItem>
              )}

              {/* Profile */}
              {(!isStaff || staffRole === "manager") && (
                <NavItem to="/profile" label="Profile">
                  <UserCircle size={24} />
                </NavItem>
              )}

              {/* Staff badge */}
              {isStaff && (
                <span className="ml-2 text-sm font-semibold bg-teal-50 text-teal-700 px-2 py-1 rounded border border-teal-200 uppercase tracking-wider text-xs">
                  {staffRole}: {staffName}
                </span>
              )}

              {/* Pull Latest Changes */}
              <NavItem label="Pull Latest Changes" onClick={handleForceUpdate}>
                <RefreshCw size={24} className="text-teal-600 animate-spin-hover" />
              </NavItem>

              {/* Logout */}
              <NavItem label="Logout" onClick={handleLogout}>
                <LogOut size={24} />
              </NavItem>
            </div>
          )}
        </div>
      </nav>

      {/* Backdrop — click outside to close the sidebar */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 z-50 right-0 h-full bg-white shadow-md transform ${
          isSidebarOpen ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 lg:hidden`}
        onClick={(e) => {
          // Close after navigating via any link/button inside the menu
          if (e.target.closest("a") || e.target.closest("button")?.textContent?.includes("Logout")) {
            setIsSidebarOpen(false);
          }
        }}
      >
        <div className="p-4">
          <button className="text-xl mb-4" onClick={toggleSidebar}>
            <i className="fas fa-times hover:text-red-600 transition duration-300"></i>
          </button>
          <div className="flex flex-col space-y-4">
            {isLoginPage && (
              <>
                <Link to="/register">Register Now</Link>
                <Link to="/">Login</Link>
              </>
            )}
            {!isLoginPage && (
              <div className="flex flex-col space-y-1">
                <Link to="/profile" className="flex items-center space-x-3 text-slate-700 hover:text-teal-600 transition-colors p-2 rounded-lg hover:bg-slate-50">
                  <UserCircle size={24} />
                  <span className="font-semibold">Profile</span>
                </Link>

                <div className="flex items-center space-x-3 text-slate-700 p-2">
                  <PhoneCall size={24} className="text-teal-600" />
                  <span className="font-bold">9099012488</span>
                </div>

                {(!isStaff || staffRole === "manager") && (
                  <Link to="/add-product" className="flex items-center space-x-3 text-slate-700 hover:text-teal-600 transition-colors p-2 rounded-lg hover:bg-slate-50">
                    <PackagePlus size={24} />
                    <span className="font-semibold">Add Product</span>
                  </Link>
                )}

                {(!isStaff || ["manager", "cashier"].includes(staffRole)) && (
                  <Link to="/history" className="flex items-center space-x-3 text-slate-700 hover:text-teal-600 transition-colors p-2 rounded-lg hover:bg-slate-50">
                    <History size={24} />
                    <span className="font-semibold">Order History</span>
                  </Link>
                )}

                {(!isStaff || ["manager", "cashier"].includes(staffRole)) && (
                  <Link to="/ledger" className="flex items-center space-x-3 text-slate-700 hover:text-teal-600 transition-colors p-2 rounded-lg hover:bg-slate-50">
                    <BookOpen size={24} />
                    <span className="font-semibold">Ledger</span>
                  </Link>
                )}

                {(!isStaff || ["manager", "cashier", "waiter"].includes(staffRole)) && (
                  <Link to="/captain" className="flex items-center space-x-3 text-slate-700 hover:text-teal-600 transition-colors p-2 rounded-lg hover:bg-slate-50">
                    <UtensilsCrossed size={24} />
                    <span className="font-semibold">Captain (Waiter Mode)</span>
                  </Link>
                )}

                {registrationType === "restaurant" &&
                  (!isStaff ||
                    ["manager", "waiter", "chef", "cashier"].includes(
                      staffRole,
                    )) && (
                    <Link to="/process" className="relative flex items-center space-x-3 text-slate-700 hover:text-teal-600 transition-colors p-2 rounded-lg hover:bg-slate-50">
                      <ChefHat size={24} />
                      <span className="font-semibold">Process</span>
                      {newOrderCount > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-[10px] font-black rounded-full px-2 py-0.5 animate-pulse">
                          {newOrderCount} new
                        </span>
                      )}
                    </Link>
                  )}

                {(!isStaff || ["manager", "chef"].includes(staffRole)) && (
                  <Link to="/kds" className="relative flex items-center space-x-3 text-slate-700 hover:text-teal-600 transition-colors p-2 rounded-lg hover:bg-slate-50">
                    <MonitorPlay size={24} />
                    <span className="font-semibold">KDS Dashboard</span>
                    {newOrderCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-black rounded-full px-2 py-0.5 animate-pulse">
                        {newOrderCount} new
                      </span>
                    )}
                  </Link>
                )}

                {(!isStaff || staffRole === "manager") && (
                  <Link to="/admin" className="flex items-center space-x-3 text-slate-700 hover:text-teal-600 transition-colors p-2 rounded-lg hover:bg-slate-50">
                    <ShieldCheck size={24} />
                    <span className="font-semibold">Admin Panel</span>
                  </Link>
                )}

                {isStaff && (
                  <div className="text-sm font-semibold text-teal-700 border-t border-b border-gray-100 py-2 my-2 uppercase tracking-wider text-xs">
                    Logged in: {staffRole} ({staffName})
                  </div>
                )}

                <button onClick={handleForceUpdate} className="flex items-center space-x-3 text-slate-700 hover:text-teal-600 transition-colors p-2 rounded-lg hover:bg-slate-50 w-full text-left mt-2">
                  <RefreshCw size={24} className="text-teal-600" />
                  <span className="font-semibold">Pull Latest Changes</span>
                </button>

                <button onClick={handleLogout} className="flex items-center space-x-3 text-slate-700 hover:text-rose-600 transition-colors p-2 rounded-lg hover:bg-rose-50 w-full text-left mt-2">
                  <LogOut size={24} />
                  <span className="font-semibold">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
